import { useState, useRef, useEffect } from "react";
import { useKey } from "react-use";
import cn from "app/util/classnames";
import HudButton from "app/ui/HudButton";

const BUTTON_SELECTED_CLASS = "text-white";
const BUTTON_UNSELECTED_CLASS = "scale-90 text-white";
const LABEL_SELECTED_CLASS = "font-semibold";
const LABEL_UNSELECTED_CLASS = "font-light";
const TRANSITION_CLASSES = "active:scale-100 transition-transform duration-200";

interface HudRadioButtonProps {
  button0Icon: React.ReactNode;
  button0Label: string;
  button1Icon: React.ReactNode;
  button1Label: string;
  onSelectionChange: (newSelection: number) => void;
  useVerticalLayout?: boolean;
  keyboardShortcut?: string;
}

export default function HudRadioButton(props: HudRadioButtonProps) {
  // Integer representing which button is currently selected
  const [selection, setSelection] = useState(0);

  // A single covering the entire element
  const buttonRef = useRef<HTMLButtonElement>(null);

  function handleClick() {
    setSelection((prev: number) => (prev === 0 ? 1 : 0));
  }

  useEffect(() => {
    props.onSelectionChange(selection);
  }, [selection]);

  useKey(
    props.keyboardShortcut ?? "",
    () => {
      // We don't want space to toggle the tool if the button is already in
      // focus, because browsers already click the focused button when space
      // is pressed.
      if (document.activeElement !== buttonRef.current) {
        handleClick();
      }
    },
    { event: "keyup" },
  );

  return (
    <button
      className="flex flex-col"
      tabIndex={-1}
      ref={buttonRef}
      onClick={handleClick}
    >
      <div
        className={cn(
          "bg-green-darker rounded-sm",
          "p-0.5",
          "flex gap-0.5",
          props.useVerticalLayout ? "flex-col" : "flex-row",
        )}
      >
        <ButtonPackage
          isSelected={selection === 0}
          icon={props.button0Icon}
          labelText={props.button0Label}
        />
        <ButtonPackage
          isSelected={selection === 1}
          icon={props.button1Icon}
          labelText={props.button1Label}
        />
      </div>
    </button>
  );
}

interface ButtonPackageProps {
  isSelected: boolean;
  icon: React.ReactNode;
  labelText: string;
  onClick?: () => void;
}

function ButtonPackage(props: ButtonPackageProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center",
        "p-0.5 rounded-sm",
        "transition-colors duration-200",
        props.isSelected ? "bg-green-light" : "",
      )}
      onMouseDown={(e) => e.preventDefault()}
    >
      <HudButton
        className={cn(
          props.isSelected ? BUTTON_SELECTED_CLASS : BUTTON_UNSELECTED_CLASS,
          TRANSITION_CLASSES,
        )}
        onClick={props.onClick}
      >
        {props.icon}
      </HudButton>
      <p
        className={cn(
          "text-xs uppercase font-sans",
          props.isSelected ? LABEL_SELECTED_CLASS : LABEL_UNSELECTED_CLASS,
        )}
      >
        {props.labelText}
      </p>
    </div>
  );
}
